import { useIntl } from 'react-intl';

import Grafico from 'src/componentes/chart';
import NpsSegmentBar from './NpsSegmentBar';
import { getZonaNps, SEGMENT_COLORS, toNumber } from './npsHelpers';

// Escala 0-10
const SCALE_COLORS = [
  '#B72224',
  '#D52029',
  '#E95223',
  '#EA6F22',
  '#F6A726',
  '#FDC729',
  '#EBDB0A',
  '#E5E144',
  '#C2D234',
  '#AFC940',
  '#66B44E',
];

const styles = {
  topRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 16,
  },
  scoreColumn: {
    // Score fica compacto; cresce pouco.
    display: 'flex',
    flex: '1 1 300px',
    minWidth: 0,
  },
  segmentosColumn: {
    // Ocupa o restante do espaço da linha.
    display: 'flex',
    flex: '2 1 400px',
    minWidth: 0,
  },
  card: {
    backgroundColor: '#fff',
    border: '1px solid #eef0f3',
    borderRadius: 12,
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.05)',
    padding: '20px 22px',
  },
  cardTitle: {
    color: '#101828',
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 18,
  },
  scoreValue: {
    color: '#101828',
    fontSize: 56,
    fontWeight: 800,
    lineHeight: 1,
  },
  scoreRange: {
    color: '#98a2b3',
    fontSize: 13,
    marginTop: 6,
  },
  zonaBadge: {
    alignSelf: 'flex-start',
    borderRadius: 999,
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    marginTop: 12,
    padding: '4px 12px',
  },
  gaugeTrack: {
    background: 'linear-gradient(90deg, #d52029 0%, #f6a726 50%, #66b44e 100%)',
    borderRadius: 999,
    height: 10,
    marginTop: 22,
    position: 'relative',
  },
  gaugeMarker: {
    background: '#101828',
    border: '2px solid #fff',
    borderRadius: '50%',
    boxShadow: '0 1px 3px rgba(16, 24, 40, 0.35)',
    height: 18,
    position: 'absolute',
    top: -4,
    transform: 'translateX(-50%)',
    width: 18,
  },
  gaugeScale: {
    color: '#98a2b3',
    display: 'flex',
    fontSize: 11,
    justifyContent: 'space-between',
    marginTop: 8,
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 20,
    marginTop: 18,
  },
  legendItem: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: 8,
  },
  legendDot: {
    borderRadius: 3,
    flexShrink: 0,
    height: 12,
    marginTop: 3,
    width: 12,
  },
  legendLabel: {
    color: '#475467',
    fontSize: 13,
    fontWeight: 600,
  },
  legendHint: {
    color: '#98a2b3',
    fontSize: 12,
  },
  legendValue: {
    color: '#101828',
    fontSize: 14,
    fontWeight: 700,
  },
  histogramAxis: {
    color: '#98a2b3',
    display: 'flex',
    fontSize: 11,
    justifyContent: 'space-between',
    marginTop: 10,
  },
};

// Faixa de uma nota NPS: detrator 0-6, neutro 7-8, promotor 9-10.
const getNotaFaixaLabelId = (nota) => {
  if (nota <= 6) return 'labelDetrator';
  if (nota <= 8) return 'labelNeutro';
  return 'labelPromotor';
};

function ScoreCard({ npsScore, totalRespostas }) {
  const intl = useIntl();
  const score = toNumber(npsScore);
  const zona = getZonaNps(score);
  const markerPercent = Math.min(Math.max((score + 100) / 200, 0), 1) * 100;

  return (
    <div
      style={{
        ...styles.card,
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}
    >
      <div style={styles.cardTitle}>
        {intl.formatMessage({ id: 'labelNpsPontuacaoGeral' })}
      </div>
      <div style={styles.scoreValue}>{score}</div>
      <div style={styles.scoreRange}>
        {intl.formatMessage(
          { id: 'labelNpsBaseadoEmRespostas' },
          { total: totalRespostas }
        )}
      </div>
      <div style={{ ...styles.zonaBadge, backgroundColor: zona.color }}>
        {intl.formatMessage({ id: zona.labelId })}
      </div>
      <div style={styles.gaugeTrack}>
        <div style={{ ...styles.gaugeMarker, left: `${markerPercent}%` }} />
      </div>
      <div style={styles.gaugeScale}>
        <span>-100</span>
        <span>0</span>
        <span>100</span>
      </div>
    </div>
  );
}

function SegmentosCard({ detratores, neutros, promotores }) {
  const intl = useIntl();

  const segmentos = [
    {
      key: 'detratores',
      dado: detratores,
      color: SEGMENT_COLORS.detratores,
      label: intl.formatMessage({ id: 'labelDetratores' }),
      hint: intl.formatMessage({ id: 'labelNpsFaixaDetratores' }),
    },
    {
      key: 'neutros',
      dado: neutros,
      color: SEGMENT_COLORS.neutros,
      label: intl.formatMessage({ id: 'labelNeutros' }),
      hint: intl.formatMessage({ id: 'labelNpsFaixaNeutros' }),
    },
    {
      key: 'promotores',
      dado: promotores,
      color: SEGMENT_COLORS.promotores,
      label: intl.formatMessage({ id: 'labelPromotores' }),
      hint: intl.formatMessage({ id: 'labelNpsFaixaPromotores' }),
    },
  ];

  const formatPercent = (value) =>
    `${intl.formatNumber(toNumber(value), { maximumFractionDigits: 1 })}%`;

  return (
    <div style={{ ...styles.card, flex: 1 }}>
      <div style={styles.cardTitle}>
        {intl.formatMessage({ id: 'labelNpsDistribuicaoRespondentes' })}
      </div>
      <NpsSegmentBar
        detratores={detratores}
        neutros={neutros}
        promotores={promotores}
        height={34}
        fontSize={12}
        radius={8}
        decimais={1}
      />
      <div style={styles.legend}>
        {segmentos.map((segmento) => (
          <div key={segmento.key} style={styles.legendItem}>
            <span
              style={{ ...styles.legendDot, backgroundColor: segmento.color }}
            />
            <div>
              <div style={styles.legendLabel}>{segmento.label}</div>
              <div style={styles.legendHint}>{segmento.hint}</div>
              <div style={styles.legendValue}>
                {formatPercent(segmento.dado?.percentual)}
                <span style={styles.legendHint}>
                  {' · '}
                  {intl.formatMessage(
                    { id: 'labelNpsQuantidadeRespostas' },
                    { quantidade: toNumber(segmento.dado?.quantidade) }
                  )}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DistribuicaoCard({ distribuicao }) {
  const intl = useIntl();
  const notas = Array.from({ length: 11 }, (_, nota) => {
    const item = (distribuicao || []).find((d) => toNumber(d.nota) === nota);

    return { nota, quantidade: toNumber(item?.quantidade) };
  });

  const chartConfig = {
    type: 'column2d',
    width: '100%',
    height: 260,
    dataFormat: 'json',
    dataSource: {
      chart: {
        theme: 'fusion',
        showValues: '1',
        showYAxisValues: '1',
        adjustDiv: '1',
        divLineAlpha: '25',
        showCanvasBorder: '0',
        showBorder: '0',
        bgAlpha: '0',
        canvasBgAlpha: '0',
        usePlotGradientColor: '0',
        showPlotBorder: '0',
        plotSpacePercent: '28',
        yAxisMinValue: '0',
        xAxisName: intl.formatMessage({ id: 'labelNpsNota' }),
        yAxisName: intl.formatMessage({ id: 'labelNpsQuantidadeRespostasEixo' }),
        labelFontColor: '#667085',
        valueFontColor: '#475467',
        baseFontSize: '12',
      },
      data: notas.map(({ nota, quantidade }) => ({
        label: String(nota),
        value: quantidade,
        color: SCALE_COLORS[nota],
        toolText: `${nota} - ${intl.formatMessage({
          id: getNotaFaixaLabelId(nota),
        })}{br}<b>${intl.formatMessage(
          { id: 'labelNpsQuantidadeRespostas' },
          { quantidade }
        )}</b>`,
      })),
    },
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>
        {intl.formatMessage({ id: 'labelNpsDistribuicaoNotas' })}
      </div>
      <Grafico {...chartConfig} />
      <div style={styles.histogramAxis}>
        <span>{intl.formatMessage({ id: 'labelDetratores' })} (0-6)</span>
        <span>{intl.formatMessage({ id: 'labelNeutros' })} (7-8)</span>
        <span>{intl.formatMessage({ id: 'labelPromotores' })} (9-10)</span>
      </div>
    </div>
  );
}

function NpsResultadoGeral({ dados }) {
  const geral = dados?.geral || dados || {};

  return (
    <div>
      <div style={styles.topRow}>
        <div style={styles.scoreColumn}>
          <ScoreCard
            npsScore={geral.npsScore}
            totalRespostas={toNumber(geral.totalRespostas)}
          />
        </div>
        <div style={styles.segmentosColumn}>
          <SegmentosCard
            detratores={geral.detratores}
            neutros={geral.neutros}
            promotores={geral.promotores}
          />
        </div>
      </div>
      <DistribuicaoCard distribuicao={geral.distribuicao} />
    </div>
  );
}

export default NpsResultadoGeral;
